import type { FeedbackCue, HapticFeedbackPort } from "./feedbackPorts";

type VibrationPattern = number | readonly number[];
type Vibrate = (pattern: VibrationPattern) => boolean;

export type WebHapticFeedbackOptions = {
  readonly enabled: boolean;
  /** Primarily a test seam; omit to use navigator.vibrate when available. */
  readonly vibrate?: Vibrate;
};

const PATTERNS: Readonly<Record<FeedbackCue, VibrationPattern>> = {
  press: 8,
  contact: 14,
  scrape: [7, 18, 7],
  lift: 12,
  confirm: [10, 28, 16],
  alert: [18, 34, 18],
};

function getBrowserVibrate(): Vibrate | undefined {
  if (
    typeof globalThis.navigator === "undefined" ||
    typeof globalThis.navigator.vibrate !== "function"
  ) {
    return undefined;
  }

  return (pattern) =>
    globalThis.navigator.vibrate(typeof pattern === "number" ? pattern : [...pattern]);
}

export function createWebHapticFeedback({
  enabled,
  vibrate = getBrowserVibrate(),
}: WebHapticFeedbackOptions): HapticFeedbackPort {
  let isEnabled = enabled;
  let isDisposed = false;

  const cancel = (): void => {
    try {
      vibrate?.(0);
    } catch {
      // Platform feedback is always best-effort.
    }
  };

  return {
    get available() {
      return vibrate !== undefined;
    },
    get enabled() {
      return isEnabled;
    },
    setEnabled(nextEnabled) {
      if (isDisposed || nextEnabled === isEnabled) {
        return;
      }

      isEnabled = nextEnabled;
      if (!isEnabled) {
        cancel();
      }
    },
    emit(cue) {
      if (isDisposed || !isEnabled || vibrate === undefined) {
        return;
      }

      try {
        vibrate(PATTERNS[cue]);
      } catch {
        // Unsupported permissions and native failures must not affect gameplay.
      }
    },
    dispose() {
      if (!isDisposed) {
        isDisposed = true;
        isEnabled = false;
        cancel();
      }
      return Promise.resolve();
    },
  };
}
